import { useAppleLogin as useAppleLoginCore, parseJwt } from '@myfunnow/web-core'
import { useAlertStore } from '@/store/alert'
import { useSocialLogin } from '@/composables/useSocialLogin'
import { QueryStringKeys } from '@/constants/commons'
import { toSafeInternalUrl } from '@/utils/common'

const APPLE_CANCEL_ERRORS = ['popup_closed_by_user', 'user_cancelled_authorize']

export function useAppleLogin() {
  const config = useRuntimeConfig()
  const { t } = useI18n()
  const alertStore = useAlertStore()
  const route = useRoute()
  const { loginWith } = useSocialLogin()

  const {
    isReady: isAppleReady,
    isLoading: isAppleLoading,
    initialize,
    signIn,
    getStoredUser,
    clearStoredUser,
  } = useAppleLoginCore({
    clientId: config.public.appleOauthClientId as string,
    redirectURI: config.public.appleOauthRedirectUri as string,
  })

  async function loginWithApple() {
    let response: Awaited<ReturnType<typeof signIn>>

    try {
      response = await signIn()
    } catch (err: unknown) {
      if (typeof err === 'object' && err !== null && 'error' in err) {
        const appleErr = err as { error: string }
        if (APPLE_CANCEL_ERRORS.includes(appleErr.error)) {
          return
        }
      }
      console.error('useAppleLogin: signIn error', err)
      alertStore.error(t('member_auth_login_failed'))
      return
    }

    const { id_token, code } = response.authorization
    let email = ''

    try {
      const decoded = parseJwt<{ email: string }>(id_token)
      email = decoded?.email ?? ''
    } catch {
      // noop
    }

    const storedUser = getStoredUser(email)
    const name = storedUser?.name ? [storedUser.name.firstName, storedUser.name.lastName].filter(Boolean).join(' ').trim() || undefined : undefined

    const rawRedirectUrl = route.query[QueryStringKeys.REDIRECT_URL]
    const safeRedirectUrl = toSafeInternalUrl(typeof rawRedirectUrl === 'string' ? rawRedirectUrl : undefined)

    const outcome = await loginWith('apple', {
      token: id_token,
      authorizationCode: code,
      name,
      redirectUrl: safeRedirectUrl,
      fallbackUrl: route.fullPath,
    })

    if (outcome === 'success') {
      clearStoredUser(email)
    }
  }

  return { loginWithApple, initialize, isAppleReady, isAppleLoading }
}
